/**
 * EnergyPage Component
 *
 * Dashboard page for electricity consumption and cost tracking.
 * Displays KPI cards, monthly cost chart, daily consumption chart,
 * and monthly data table. Includes refresh button for on-demand
 * sync from Octopus Energy API.
 */

import { useState } from 'react'
import { PageLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useEnergy } from '@/hooks/useEnergy'
import { useTariffComparison, TARIFF_OPTIONS } from '@/hooks/useTariffComparison'
import EnergyKpiCards from '@/components/energy/EnergyKpiCards'
import EnergyMonthlyChart from '@/components/energy/EnergyMonthlyChart'
import EnergyDailyChart from '@/components/energy/EnergyDailyChart'
import EnergyMonthlyTable from '@/components/energy/EnergyMonthlyTable'
import AgileComparisonChart from '@/components/energy/AgileComparisonChart'
import BatteryComparisonTab from '@/components/energy/BatteryComparisonTab'
import SolarComparisonTab from '@/components/energy/SolarComparisonTab'
import EnergyComparisonTab from '@/components/energy/EnergyComparisonTab'

export default function EnergyPage({ user, onLogout }) {
  const [tariff, setTariff] = useState('agile')

  const {
    meters,
    daily,
    monthly,
    isLoading,
    isSyncing,
    error,
    syncEnergy
  } = useEnergy()

  const {
    data: tariffData,
    isLoading: isTariffLoading,
    error: tariffError
  } = useTariffComparison({ tariff })

  const vatRate = meters?.[0]?.vat_rate ?? 0.20
  const tariffLabel = TARIFF_OPTIONS.find(o => o.value === tariff)?.label ?? tariff

  const handleSync = async () => {
    try {
      const result = await syncEnergy()
      if (result?.readings_synced > 0) {
        toast.success(`Synced ${result.readings_synced} new readings`)
      } else {
        toast.info('No new readings available')
      }
    } catch (err) {
      toast.error('Sync failed', { description: err.message })
    }
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Energy</h1>
            <p className="text-muted-foreground mt-1">
              Electricity consumption and costs
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Latest'}
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">
                Failed to load energy data: {error}
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tariff">Tariff Comparison</TabsTrigger>
            <TabsTrigger value="battery">Battery</TabsTrigger>
            <TabsTrigger value="solar">Solar</TabsTrigger>
            <TabsTrigger value="compare">Compare</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <EnergyKpiCards
              monthly={monthly}
              meters={meters}
              isLoading={isLoading}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EnergyMonthlyChart
                monthly={monthly}
                vatRate={vatRate}
                isLoading={isLoading}
              />
              <EnergyDailyChart
                daily={daily}
                isLoading={isLoading}
              />
            </div>

            <EnergyMonthlyTable
              monthly={monthly}
              vatRate={vatRate}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="tariff" className="space-y-6">
            {tariffError && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <p className="text-red-800">
                    Failed to load tariff comparison: {tariffError}
                  </p>
                </CardContent>
              </Card>
            )}
            <AgileComparisonChart
              data={tariffData}
              isLoading={isTariffLoading}
              tariff={tariff}
              tariffLabel={tariffLabel}
              onTariffChange={setTariff}
            />
          </TabsContent>

          <TabsContent value="battery" className="space-y-6">
            <BatteryComparisonTab
              vatRate={vatRate}
              tariff={tariff}
              tariffLabel={tariffLabel}
              onTariffChange={setTariff}
            />
          </TabsContent>

          <TabsContent value="solar" className="space-y-6">
            <SolarComparisonTab
              vatRate={vatRate}
              tariff={tariff}
              tariffLabel={tariffLabel}
              onTariffChange={setTariff}
            />
          </TabsContent>

          <TabsContent value="compare" className="space-y-6">
            <EnergyComparisonTab />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  )
}
